import { Fragment, useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition } from '@headlessui/react';
import { XMarkIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { AlertStatus, AlertSeverity, AlertType } from '../api/alertService';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import type { Gateway, Customer } from '../context/ManagementContext';

interface FilterCriteria {
  status: AlertStatus | 'all';
  severity: AlertSeverity | 'all';
  type: AlertType | 'all';
  gatewayId: string | 'all';
  customerId: string | 'all';
  sortBy: 'newest' | 'oldest' | 'severity' | 'status';
}

interface AlertAdvancedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterCriteria) => void;
  currentFilters: FilterCriteria;
}

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: AlertStatus.ACTIVE, label: 'Active' },
  { value: AlertStatus.ACKNOWLEDGED, label: 'Acknowledged' },
  { value: AlertStatus.RESOLVED, label: 'Resolved' },
];

const severityOptions = [
  { value: 'all', label: 'All Severities' },
  { value: AlertSeverity.CRITICAL, label: 'Critical' },
  { value: AlertSeverity.HIGH, label: 'High' },
  { value: AlertSeverity.MEDIUM, label: 'Medium' },
  { value: AlertSeverity.LOW, label: 'Low' },
];

const typeOptions = [
  { value: 'all', label: 'All Types' },
  { value: AlertType.DATA_GAP, label: 'Data Gap' },
  { value: AlertType.GATEWAY_OFFLINE, label: 'Gateway Offline' },
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'severity', label: 'By Severity' },
  { value: 'status', label: 'By Status' },
];

export default function AlertAdvancedFilterModal({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters
}: AlertAdvancedFilterModalProps) {
  const [filters, setFilters] = useState<FilterCriteria>(currentFilters);
  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showGatewaySelector, setShowGatewaySelector] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters, isOpen]);

  const handleApply = () => {
    onApplyFilters({
      ...filters,
      gatewayId: selectedGateway?.id || 'all',
      customerId: selectedCustomer?.id || 'all',
    });
    onClose();
  };

  const handleReset = () => {
    const resetFilters: FilterCriteria = {
      status: 'all',
      severity: 'all',
      type: 'all',
      gatewayId: 'all',
      customerId: 'all',
      sortBy: 'newest',
    };
    setFilters(resetFilters);
    setSelectedGateway(null);
    setSelectedCustomer(null);
  };

  const updateFilter = (key: keyof FilterCriteria, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle as="h3" className="text-lg font-medium text-gray-900 flex items-center">
                    <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2" />
                    Advanced Alert Filters
                  </DialogTitle>
                  <button
                    onClick={onClose}
                    className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Status and Type Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={filters.status}
                        onChange={(e) => updateFilter('status', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      <select
                        value={filters.type}
                        onChange={(e) => updateFilter('type', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        {typeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Severity and Sort Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                      <select
                        value={filters.severity}
                        onChange={(e) => updateFilter('severity', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        {severityOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                      <select
                        value={filters.sortBy}
                        onChange={(e) => updateFilter('sortBy', e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Gateway and Customer Selectors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowGatewaySelector(true)}
                          className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <span className="block truncate">
                            {selectedGateway ? (selectedGateway.name || selectedGateway.id) : 'Select a gateway...'}
                          </span>
                        </button>
                        {selectedGateway && (
                          <button
                            type="button"
                            onClick={() => setSelectedGateway(null)}
                            className="absolute inset-y-0 right-0 flex items-center pr-2"
                          >
                            <span className="text-gray-400 hover:text-gray-600">×</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowCustomerSelector(true)}
                          className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <span className="block truncate">
                            {selectedCustomer ? selectedCustomer.name : 'Select a customer...'}
                          </span>
                        </button>
                        {selectedCustomer && (
                          <button
                            type="button"
                            onClick={() => setSelectedCustomer(null)}
                            className="absolute inset-y-0 right-0 flex items-center pr-2"
                          >
                            <span className="text-gray-400 hover:text-gray-600">×</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Active Filters Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Active Filters:</h4>
                    <div className="flex flex-wrap gap-2">
                      {filters.status !== 'all' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Status: {statusOptions.find(o => o.value === filters.status)?.label}
                        </span>
                      )}
                      {filters.severity !== 'all' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Severity: {severityOptions.find(o => o.value === filters.severity)?.label}
                        </span>
                      )}
                      {filters.type !== 'all' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Type: {typeOptions.find(o => o.value === filters.type)?.label}
                        </span>
                      )}
                      {selectedGateway && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Gateway: {selectedGateway.name || selectedGateway.id}
                        </span>
                      )}
                      {selectedCustomer && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          Customer: {selectedCustomer.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex justify-between">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Reset All
                  </button>
                  
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleApply}
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>

        {/* Gateway Selector Modal */}
        <AdvancedEntitySelector
          isOpen={showGatewaySelector}
          onClose={() => setShowGatewaySelector(false)}
          onSelect={(entity) => {
            setSelectedGateway(entity as Gateway);
            setShowGatewaySelector(false);
          }}
          entityType="gateway"
          title="Select Gateway"
        />

        {/* Customer Selector Modal */}
        <AdvancedEntitySelector
          isOpen={showCustomerSelector}
          onClose={() => setShowCustomerSelector(false)}
          onSelect={(entity) => {
            setSelectedCustomer(entity as Customer);
            setShowCustomerSelector(false);
          }}
          entityType="customer"
          title="Select Customer"
        />
      </Dialog>
    </Transition>
  );
}